import { HttpClient, HttpContext } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENV_CONFIG } from '@digilize/shared/utils/tokens';
import { DeliveryType, EnvConfig } from '@shared/definitions';
import { map, Observable } from 'rxjs';

import { dateOnlyFromApi } from '../application/date-only';
import { SKIP_ERROR_INTERCEPTOR } from '../application/errors/error.interceptor';
import {
  cartRequiresAccount,
  SubscriptionBillingPeriod,
  SubscriptionProduct,
  SubscriptionStartDateRequirement,
} from '../models/subscription';
import { CartSummaryLineDto, ProductReservationResponse } from './dto/products.dto';
import {
  CartLineSubscriberEditDto,
  CartLineSubscriberValidationResultDto,
  SubscriptionBillingPeriodListDto,
  SubscriptionProductListDto,
} from './dto/subscription.dto';

export interface SubscriptionCartLine {
  cartId: string;
  cartLineId: string;
  total: number;
  /** Backend-computed "Totaal €X per maand" figure (`summary.total_recurring_monthly`). */
  totalRecurringMonthly: number;
  /** Every line in the cart afterwards — carries the per-line setup fee the summary breaks out. */
  lines: CartSummaryLineDto[];
  /**
   * Whether anything now in the cart may only be bought by a logged-in buyer
   * (`summary.products[].requires_account`) — what the account step's guest option hangs off.
   */
  requiresAccount: boolean | null;
  /** When the cart's hold runs out — only set when the POS has an order time limit enabled. */
  reservedUntil: string | null;
  /** Backend clock at the time of the reservation; the countdown is measured against it. */
  serverTime: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class SubscriptionShopService {
  private readonly baseUrl = `${this.config.api}/v1.0`;

  constructor(@Inject(ENV_CONFIG) private readonly config: EnvConfig, private readonly client: HttpClient) {}

  /** GET v1.0/subscription/products?pos={posId} — active subscription products for a POS. */
  getProducts(posId: string): Observable<SubscriptionProduct[]> {
    return this.client
      .get<SubscriptionProductListDto[]>(`${this.baseUrl}/subscription/products`, { params: { pos: posId } })
      .pipe(map((items) => (items ?? []).map((item) => this.mapToProduct(item))));
  }

  /**
   * Adds a subscription product to the cart via the generic reservations endpoint.
   * Subscriptions have no show, so show_id/seat_id are omitted; cartId is null for the first line.
   * `billingPeriodId` is mandatory for recurring subscriptions — the endpoint rejects the
   * reservation with `subscription_billing_period_required` when it is missing.
   */
  addToCart(
    productId: string,
    posId: string,
    cartId: string | null,
    billingPeriodId: string | null
  ): Observable<SubscriptionCartLine> {
    return this.client
      .post<ProductReservationResponse>(`${this.baseUrl}/reservations`, {
        product_id: productId,
        pos_id: posId,
        cart_id: cartId,
        delivery_method: DeliveryType.Digital,
        billing_period_id: billingPeriodId,
      })
      .pipe(
        map((res) => ({
          cartId: res.cart_id,
          cartLineId: res.cart_line_id,
          total: res.cart?.summary?.total_amount ?? 0,
          totalRecurringMonthly: res.cart?.summary?.total_recurring_monthly ?? 0,
          lines: res.cart?.summary?.lines ?? [],
          requiresAccount: cartRequiresAccount(res.cart?.summary?.products),
          reservedUntil: res.cart?.reserved_until ?? null,
          serverTime: res.cart?.server_time ?? null,
        }))
      );
  }

  /** PUT v1.0/subscription/cart/edit/{cartLineId} — set subscriber data, start date, billing period. */
  editCartLine(cartLineId: string, body: CartLineSubscriberEditDto): Observable<void> {
    return this.client.put<void>(`${this.baseUrl}/subscription/cart/edit/${cartLineId}`, body, {
      context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true),
    });
  }

  /** POST v1.0/subscription/cart/{cartId}/validate — per-line validation results. */
  validateCart(cartId: string): Observable<CartLineSubscriberValidationResultDto[]> {
    return this.client
      .post<CartLineSubscriberValidationResultDto[]>(`${this.baseUrl}/subscription/cart/${cartId}/validate`, null, {
        context: new HttpContext().set(SKIP_ERROR_INTERCEPTOR, true),
      })
      .pipe(map((results) => results ?? []));
  }

  private mapToProduct(dto: SubscriptionProductListDto): SubscriptionProduct {
    return {
      id: dto.id,
      name: dto.public_name ?? '',
      subtitle: dto.subtitle,
      description: dto.description,
      type: dto.subscription_type,
      requiresAccount: dto.requires_account,
      // An absent level reads as "not required", which is both the backend's default and what every
      // product did before the field existed: no picker, dated on the day of purchase.
      startDateRequirement: dto.start_date_requirement ?? SubscriptionStartDateRequirement.NotRequired,
      photoUrl: dto.photo?.url1x ?? null,
      photoAlt: dto.photo?.alt ?? null,
      price: dto.price ?? 0,
      priceBefore: dto.original_price,
      taxRate: dto.tax_rate,
      billingPeriods: (dto.billing_periods ?? []).map((period) => this.mapToBillingPeriod(period)),
      sepaDirectDebitEnabled: dto.sepa_direct_debit_enabled,
      sepaTermsAndConditionsUrl: dto.sepa_terms_and_conditions_url,
      sepaDescriptionHtml: dto.sepa_description_html,
      isSoldOut: dto.is_sold_out,
    };
  }

  private mapToBillingPeriod(dto: SubscriptionBillingPeriodListDto): SubscriptionBillingPeriod {
    return {
      id: dto.id,
      periodType: dto.period_type,
      price: dto.price ?? 0,
      priceBefore: dto.original_price,
      taxRate: dto.tax_rate,
      pricePerMonth: dto.price_per_month,
      savingsPercentage: dto.savings_percentage,
      // Read off the string, like every other date this flow carries, so no timezone can shift the day.
      firstAllowedStartDate: dto.first_allowed_start_date ? dateOnlyFromApi(dto.first_allowed_start_date) : null,
    };
  }
}
